import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import {
  ChatTypingVisitorUpdate,
  Conversation,
  SubscribeUnsubscribeData,
} from 'sb-lib/types';
import { getChats, getConversationMessages, patchChatMessage } from 'shared/api';
import {
  sendSocketChatMessage,
  sendSocketReadChatMessage,
  sendSocketChatTyping,
} from 'shared/realtime';
import { uploadSignedPostWithProgress } from 'shared/utils/s3';
import {
  ChatMessage,
  ChatMessageArgs,
  ChatMessagesPage,
  ChatMessageTextBlock,
  ChatMessageUpdatePayload,
  User,
} from 'types';
import { AppState } from './reducers';
import { selectActiveUser } from './shared-selectors';
import { userToAgent } from 'shared/utils/user-agent';
import log from 'shared/utils/log';
import { SbSocketErrorCode } from 'sb-lib/sb-socket-abstract';
import { setIsShowingChatUsageLimit } from 'shared/store/dashboard-slice';

// STATE
export const initialState = {
  isFetchingChatMessages: false,
  chatMessages: [] as ChatMessage[],
  chatMessagesCursor: null as number | null,
  chatMessagesVisitorId: null as string | null,
  chatMessagesConversationId: null as number | null,
  chatImageUploadDataUrl: null as string | null,
  chatImageUploadDataUrlDimensions: null as { w: number; h: number } | null,
  chatImageUploadId: null as string | null,
  chatImageUploadProgress: 0,
  chatImageUploadError: null as string | null,
  conversationTypingState: {} as Record<
    Conversation['id'],
    ChatTypingVisitorUpdate & { ts: number }
  >,
};

export type ChatState = typeof initialState;

// THUNKS

export const sendChatMessage = createAsyncThunk<
  ChatMessage,
  ChatMessageArgs,
  { state: AppState }
>('chat/sendChatMessage', async (args, { requestId, dispatch, getState }) => {
  const state = getState();
  const user = selectActiveUser(state);
  if (!user || !args.visitorId) {
    throw new Error('Must be logged in to send chat');
  }

  const message = args.registeredImg
    ? ({
        conversationId: args.conversationId,
        content: args.content,
        imageIds: [args.registeredImg.id],
      } as ChatMessageArgs)
    : args;

  // Provides extra state that isn't included in `pending` action.
  dispatch(
    addPendingChatMessage({
      requestId,
      user,
      visitorId: args.visitorId,
      message,
    }),
  );

  // Upload any image
  if (args.registeredImg && args.file && args.conversationId) {
    dispatch(sendChatTyping({ isTyping: true, conversationId: args.conversationId }));
    try {
      const uploadRes = await uploadSignedPostWithProgress(
        args.file,
        args.registeredImg.uploadUrl,
        (percent: number) => {
          dispatch(chatImageUploadProgressChanged(percent));
        },
      );
      if (uploadRes.status !== 204) {
        const error = `Failed to upload image (${uploadRes.status})`;
        dispatch(chatImageUploadErrorChanged(error));
        throw new Error(error);
      }
    } catch (e) {
      let message = 'unknown reason';
      if ('message' in e) {
        message = (e as { message: string }).message;
      }
      dispatch(chatImageUploadErrorChanged(`Image upload failed: ${message}`));
      throw new Error(`Image upload failed: ${message}`);
    }
  }

  let res;
  try {
    res = await sendSocketChatMessage(message);
  } catch (e) {
    if (e === SbSocketErrorCode.LIMIT_REACHED) {
      dispatch(setIsShowingChatUsageLimit(true));
      throw new Error('New conversation limit reached');
    } else {
      throw e;
    }
  }

  if (!res) {
    throw new Error('No connection to server');
  }
  return res.data;
});

export const sendChatTyping = createAsyncThunk<
  void,
  { isTyping: boolean; conversationId: number },
  { state: AppState }
>('chat/sendChatTyping', ({ isTyping, conversationId }, { getState }) => {
  const user = selectActiveUser(getState());
  if (user) {
    sendSocketChatTyping(isTyping, conversationId, userToAgent(user));
  }
});

export const fetchMoreChatMessages = createAsyncThunk<
  ChatMessagesPage & { reset: boolean },
  { visitorId: string; conversationId: number },
  { state: AppState }
>(
  'chat/fetchMoreChatMessages',
  async ({ visitorId, conversationId }, { getState }) => {
    const state = getState();
    const {
      chat: { chatMessagesCursor, chatMessagesVisitorId, chatMessagesConversationId },
    } = state;
    const results = conversationId
      ? await getConversationMessages(conversationId, chatMessagesCursor)
      : await getChats(visitorId, chatMessagesCursor);
    const reset =
      visitorId !== chatMessagesVisitorId ||
      conversationId !== chatMessagesConversationId;
    return { ...results.data, reset };
  },
  {
    condition: ({ visitorId, conversationId }, { getState }) => {
      const {
        chatMessagesConversationId,
        chatMessagesVisitorId,
        isFetchingChatMessages,
      } = getState().chat;
      const alreadyLoading =
        isFetchingChatMessages &&
        chatMessagesVisitorId === visitorId &&
        chatMessagesConversationId === conversationId;
      return !alreadyLoading;
    },
  },
);

export const updateChatMessage = createAsyncThunk<
  ChatMessage,
  { id: string; payload: ChatMessageUpdatePayload },
  { state: AppState }
>('conversation/updateChatMessage', async ({ id, payload }) => {
  const res = await patchChatMessage(id, payload);
  return res.data;
});

// SLICE
const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    markChatMessageRead: (_, action: PayloadAction<{ messageId: string }>) => {
      sendSocketReadChatMessage({ [action.payload.messageId]: true });
    },
    receivedConversationChatMessage: (
      state,
      action: PayloadAction<SubscribeUnsubscribeData & { message: ChatMessage }>,
    ) => {
      const chatMessagesConversationId = state.chatMessagesConversationId;
      log.debug(`receivedConversationChatMessage id: ${chatMessagesConversationId}`);
      const { channelIdentifier, message } = action.payload;
      if (channelIdentifier === chatMessagesConversationId?.toString()) {
        state.chatMessages = [
          ...state.chatMessages.filter(msg => msg.id !== message.id),
          message,
        ];
      }
    },
    receivedConversationVisitorTypingMessage: (
      state,
      action: PayloadAction<ChatTypingVisitorUpdate>,
    ) => {
      const { conversationId } = action.payload;
      state.conversationTypingState[conversationId] = {
        ...action.payload,
        ts: Date.now(),
      };
    },
    addPendingChatMessage(
      state,
      action: PayloadAction<{
        requestId: string;
        message: ChatMessageArgs;
        user: User;
        visitorId: string;
      }>,
    ) {
      // Optimistically add message to list (if we can assemble it locally)
      const { message, user, requestId, visitorId } = action.payload;
      const text = (message.content.blocks[0] as ChatMessageTextBlock)?.text || '';
      state.chatMessages = [
        ...state.chatMessages,
        {
          id: `pending-msg-${requestId}`,
          conversationId: message.conversationId,
          pending: true,
          timestamp: Date.now(),
          from: user.id,
          visitorId,
          agent: {
            id: user.id,
            name: user.displayName,
            title: user.title,
            avatar: user.avatar?.imageUrl,
          },
          content: message.content,
          imageIds: message.imageIds,
          text,
          sourceType: 'agent',
          dateRead: null,
          moderationStatus: null,
          overrideModerationStatus: false,
        },
      ];
    },
    chatImageUploadDataUrlChanged: (
      state,
      action: PayloadAction<
        Pick<ChatState, 'chatImageUploadDataUrl' | 'chatImageUploadDataUrlDimensions'>
      >,
    ) => {
      state.chatImageUploadDataUrl = action.payload.chatImageUploadDataUrl;
      state.chatImageUploadDataUrlDimensions =
        action.payload.chatImageUploadDataUrlDimensions;
    },
    chatImageUploadIdChanged: (
      state,
      action: PayloadAction<ChatState['chatImageUploadId']>,
    ) => {
      state.chatImageUploadId = action.payload;
    },
    chatImageUploadProgressChanged: (
      state,
      action: PayloadAction<ChatState['chatImageUploadProgress']>,
    ) => {
      state.chatImageUploadProgress = action.payload;
    },
    chatImageUploadErrorChanged: (
      state,
      action: PayloadAction<ChatState['chatImageUploadError']>,
    ) => {
      state.chatImageUploadError = action.payload;
    },
    clearChatMessages(state) {
      state.chatMessages = [];
      state.chatMessagesCursor = null;
    },
  },
  extraReducers: builder => {
    // fetchMoreChatMessages
    builder
      .addCase(fetchMoreChatMessages.pending, (state, action) => {
        state.isFetchingChatMessages = true;
        state.chatMessagesVisitorId = action.meta.arg.visitorId;
        state.chatMessagesConversationId = action.meta.arg.conversationId;
      })
      .addCase(fetchMoreChatMessages.fulfilled, (state, action) => {
        const { items, cursor, reset } = action.payload;
        state.isFetchingChatMessages = false;
        state.chatMessages = reset ? items : state.chatMessages.concat(items);
        state.chatMessagesCursor = cursor;
        log.debug(
          `fetchMoreChatMessages.fulfilled added ${items.length} messages, total messages ${state.chatMessages}`,
        );
      })
      .addCase(fetchMoreChatMessages.rejected, state => {
        state.isFetchingChatMessages = false;
        state.chatMessagesCursor = null;
      });

    // sendChatMessage
    builder
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        // Remove optimistic message, add real one
        state.chatMessages = [
          ...state.chatMessages.filter(msg => msg.id !== action.payload.id),
          action.payload,
        ].filter(msg => msg.id !== `pending-msg-${action.meta.requestId}`);
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        // Remove optimistic message
        state.chatMessages = state.chatMessages.filter(
          msg => msg.id !== `pending-msg-${action.meta.requestId}`,
        );
      });

    builder.addCase(updateChatMessage.fulfilled, (state, action) => {
      const payload = action.payload;
      state.chatMessages = state.chatMessages.map(msg => {
        if (msg.id === payload.id) {
          return {
            ...msg,
            ...payload,
          };
        }
        return msg;
      });
    });
  },
});

// SELECTORS
export const VISITOR_TYPING_CHAT_MESSAGE_ID = 'visitor-typing';
export const selectVisitorChatMessages = createSelector(
  (state: AppState) => state.chat.chatMessages,
  (state: AppState) => state.chat.conversationTypingState,
  (_state: AppState, visitorId: string | undefined) => visitorId,
  (chatMessages, typingMap, visitorId) => {
    if (!visitorId) return [];
    const list = chatMessages
      .filter(msg => msg.visitorId === visitorId)
      .sort((a, b) => {
        // Prioritize pending messages, in case the user's local time is
        // mismatched with server time, to prevent messages teleporting.
        if (a.pending !== b.pending) {
          return a.pending ? -1 : 1;
        }
        return b.timestamp - a.timestamp;
      });

    log.debug(
      `selectVisitorChatMessages chatMessages: ${chatMessages.length} filtered: ${list.length} visitorId: ${visitorId}`,
    );

    // If the visitor is typing for this convo, prepend "typing" message
    if (list.length && list[0].conversationId in typingMap) {
      const { conversationId } = list[0];
      const typingState = typingMap[conversationId];
      const isLastNewer = list[0].timestamp > typingState.ts;
      if (typingState.isTyping && !isLastNewer) {
        list.unshift({
          sourceType: 'visitor',
          id: VISITOR_TYPING_CHAT_MESSAGE_ID,
          from: visitorId,
          visitorId,
          agent: { id: 0, name: '', title: '' },
          text: '',
          content: { blocks: [] },
          timestamp: Date.now(),
          conversationId,
          dateRead: new Date().toISOString(),
          moderationStatus: null,
          overrideModerationStatus: false,
        });
      }
    }

    return list;
  },
);

export const selectHasMoreChatMessages = createSelector(
  (state: AppState) => state.chat.chatMessagesCursor,
  chatMessagesCursor => !!chatMessagesCursor,
);

// EXPORTS
export const {
  markChatMessageRead,
  receivedConversationChatMessage,
  receivedConversationVisitorTypingMessage,
  addPendingChatMessage,
  chatImageUploadDataUrlChanged,
  chatImageUploadIdChanged,
  chatImageUploadProgressChanged,
  chatImageUploadErrorChanged,
  clearChatMessages,
} = chatSlice.actions;
export default chatSlice.reducer;
